import type { Address } from '@core/generated/gql-types';
import { generateFriendlyAddress } from '@core/util/google-places.service';

/** Custom attribute refs not shown in Personal Information. */
export const HIDDEN_CUSTOM_ATTRIBUTE_REFS: string[] = [
  'validVisa',
  'onboardingPercentage',
  'onboardingCompleted',
];

export const AHPRA_REGISTRATION_NUMBER_REF = 'ahpraRegistrationNumberNoChild';
export const AHPRA_REGISTRATION_NUMBER_LABEL = 'AHPRA Registration Number';

const EXPLICIT_CUSTOM_ATTRIBUTE_LABEL_BY_REF: Record<string, string> = {
  [AHPRA_REGISTRATION_NUMBER_REF]: AHPRA_REGISTRATION_NUMBER_LABEL,
};

/** Rendered explicitly in Personal Information (excluded from the dynamic list). */
export const EXPLICIT_CUSTOM_ATTRIBUTE_REFS: string[] = Object.keys(
  EXPLICIT_CUSTOM_ATTRIBUTE_LABEL_BY_REF,
);

export const getExplicitCustomAttributeLabel = (ref: string): string =>
  EXPLICIT_CUSTOM_ATTRIBUTE_LABEL_BY_REF[ref] ?? ref;

const ADDRESS_JSON_KEYS = [
  'addressFriendly',
  'addressLine1',
  'addressLine2',
  'suburb',
  'state',
  'postcode',
  'countryLabel',
  'countryValue',
] as const;

const readAddressString = (
  record: Record<string, unknown>,
  key: (typeof ADDRESS_JSON_KEYS)[number],
): string | undefined => {
  const value = record[key];
  return typeof value === 'string' ? value : undefined;
};

/** Parses a custom-attribute value that stores an address as JSON. */
export const parseAddressFromJson = (addressJson: string): Address | null => {
  const trimmed = addressJson.trim();
  if (!trimmed.startsWith('{')) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    const record = parsed as Record<string, unknown>;
    const hasAddressField = ADDRESS_JSON_KEYS.some(
      (key) => typeof record[key] === 'string',
    );
    if (!hasAddressField) {
      return null;
    }

    return {
      addressFriendly: readAddressString(record, 'addressFriendly'),
      addressLine1: readAddressString(record, 'addressLine1'),
      addressLine2: readAddressString(record, 'addressLine2'),
      suburb: readAddressString(record, 'suburb'),
      state: readAddressString(record, 'state'),
      postcode: readAddressString(record, 'postcode'),
      countryLabel: readAddressString(record, 'countryLabel'),
      countryValue: readAddressString(record, 'countryValue'),
    };
  } catch {
    return null;
  }
};

export const formatAddressJsonToAddressFriendlyText = (
  addressJson: string,
): string => {
  const address = parseAddressFromJson(addressJson);
  if (!address) {
    return '';
  }
  return generateFriendlyAddress(address);
};

export const addressToAddressJson = (address: Address): string =>
  JSON.stringify({
    addressLine1: address.addressLine1 || '',
    addressLine2: address.addressLine2 || '',
    suburb: address.suburb || '',
    state: address.state || '',
    postcode: address.postcode || '',
    countryLabel: address.countryLabel || '',
    countryValue: address.countryValue || '',
    addressFriendly:
      address.addressFriendly || generateFriendlyAddress(address) || '',
  });
